import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ProfileLayout from "@/components/ProfileLayout.jsx";
import {
    createProduct,
    deleteProduct,
    getAdminCategories,
    getAdminLotNames,
    publishProduct,
    searchAdminProducts,
    updateProduct,
    uploadFile
} from "@/api/admin.api.js";
import { getCountries } from "@/api/public.api.js";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll.js";
import { formatProductStatus, productStatusClassName } from "@/utils/format.js";
import Price from "@/components/Price.jsx";
import { lowestProductPrice, validateVolumePriceRanges } from "@/utils/productPricing.js";
import { PRODUCT_STATUSES } from "@/utils/constants.js";
import ProductFilterToolbox from "@/components/ProductFilterToolbox.jsx";
import ProductImageGallery from "@/components/ProductImageGallery.jsx";
import CategoryField from "@/components/CategoryField.jsx";
import CountryField from "@/components/CountryField.jsx";
import IOSToggle from "@/components/IOSToggle.jsx";
import CountryFlag from "@/components/CountryFlag.jsx";
import { countryDisplayName } from "@/utils/countryDisplay.js";
import ConfirmDialog from "@/components/ConfirmDialog.jsx";
import RowActionsMenu from "@/components/RowActionsMenu.jsx";
import BackLink from "@/components/BackLink.jsx";
import { EMPTY_PRODUCT_FILTERS } from "@/utils/productFilters.js";
import { ADMIN_PROFILE_DEFAULT_SECTION, staffProfileMenuForRole } from "@/utils/profileMenus.js";
import AdminOrdersSection from "@/pages/admin-profile/AdminOrdersSection.jsx";
import AdminLotsSection from "@/pages/admin-profile/AdminLotsSection.jsx";
import AdminProductLots from "@/pages/admin-profile/AdminProductLots.jsx";
import AdminCustomersSection from "@/pages/admin-profile/AdminCustomersSection.jsx";
import AdminCartsSection from "@/pages/admin-profile/AdminCartsSection.jsx";
import AdminVoeuxSection from "@/pages/admin-profile/AdminVoeuxSection.jsx";
import AdminReviewsSection from "@/pages/admin-profile/AdminReviewsSection.jsx";
import AdminAdvertisementsSection from "@/pages/admin-profile/AdminAdvertisementsSection.jsx";
import AdminWebsiteSection from "@/pages/admin-profile/AdminWebsiteSection.jsx";
import AdminAccountSection from "@/pages/admin-profile/AdminAccountSection.jsx";
import AdminEmployeesSection from "@/pages/admin-profile/AdminEmployeesSection.jsx";
import { useAuth } from "@/auth/AuthContext.jsx";
import { canDeleteProduct, canWriteProductPriceAndStatus, staffMenuSectionAllowed, STAFF_ROLE_LABELS } from "@/utils/staffAccess.js";
import "@/pages/admin-profile/opening-hours.css";
import "@/pages/admin-profile/website-settings.css";
import "@/pages/customer-profile/profile.css";
import "@/components/product-filter.css";

const emptyProduct = {
    title: "",
    description: "",
    price: "",
    hasVolumePricing: false,
    volumePrices: [{ minQuantity: 1, maxQuantity: "", price: "" }],
    category: "",
    countryOfOriginId: null,
    showCountryBannerOnDetail: false,
    stockQuantity: 0,
    photoIds: [],
    status: "CREATED"
};

function productToForm(product) {
    const volumePrices = product.hasVolumePricing && product.volumePrices?.length
        ? product.volumePrices.map((tier) => ({
            minQuantity: tier.minQuantity,
            maxQuantity: tier.maxQuantity ?? "",
            price: tier.price
        }))
        : [{ minQuantity: 1, maxQuantity: "", price: product.price ?? "" }];
    return {
        title: product.title || "",
        description: product.description || "",
        price: product.price ?? "",
        hasVolumePricing: !!product.hasVolumePricing,
        volumePrices,
        category: product.category || "",
        countryOfOriginId: product.countryOfOrigin?.id || null,
        showCountryBannerOnDetail: !!product.showCountryBannerOnDetail,
        stockQuantity: product.stockQuantity ?? 0,
        photoIds: product.photoIds?.length
            ? [...product.photoIds]
            : (product.photoId ? [product.photoId] : []),
        status: product.status || "CREATED"
    };
}

function formToProductSnapshot(editingId, form, previous = null, countries = []) {
    return {
        ...(previous || {}),
        id: editingId,
        title: form.title,
        description: form.description,
        price: Number(form.price),
        hasVolumePricing: !!form.hasVolumePricing,
        volumePrices: form.hasVolumePricing
            ? form.volumePrices.map((tier) => ({
                minQuantity: Number(tier.minQuantity),
                maxQuantity: tier.maxQuantity === "" || tier.maxQuantity == null
                    ? undefined
                    : Number(tier.maxQuantity),
                price: Number(tier.price)
            }))
            : undefined,
        category: form.category,
        countryOfOrigin: form.countryOfOriginId
            ? (previous?.countryOfOrigin?.id === form.countryOfOriginId
                ? previous.countryOfOrigin
                : countries.find((entry) => entry.id === form.countryOfOriginId) || { id: form.countryOfOriginId })
            : null,
        showCountryBannerOnDetail: !!form.showCountryBannerOnDetail,
        stockQuantity: form.stockQuantity,
        photoIds: [...form.photoIds],
        photoId: form.photoIds[0] || null,
        status: form.status
    };
}

export default function AdminProfile() {
    const { role } = useAuth();
    const allowProductDelete = canDeleteProduct(role);
    const allowProductPriceAndStatus = canWriteProductPriceAndStatus(role);
    const menuItems = staffProfileMenuForRole(role);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [countries, setCountries] = useState([]);
    const [lotNames, setLotNames] = useState([]);
    const [draftFilters, setDraftFilters] = useState(EMPTY_PRODUCT_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(EMPTY_PRODUCT_FILTERS);
    const [page, setPage] = useState(0);
    const [hasNext, setHasNext] = useState(false);
    const [productsLoading, setProductsLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [form, setForm] = useState(emptyProduct);
    const [editingId, setEditingId] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [productViewMode, setProductViewMode] = useState("list");
    const [activeSection, setActiveSection] = useState(null);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [pendingDeleteProductId, setPendingDeleteProductId] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const appliedFiltersRef = useRef(appliedFilters);
    appliedFiltersRef.current = appliedFilters;

    useEffect(() => {
        if (!message && !error) {
            return undefined;
        }
        const timer = window.setTimeout(() => {
            setMessage("");
            setError("");
        }, 8000);
        return () => window.clearTimeout(timer);
    }, [message, error]);

    const fetchProductsPage = useCallback(async (pageToLoad, filters, append) => {
        if (append) {
            setLoadingMore(true);
        } else {
            setProductsLoading(true);
        }
        try {
            const response = await searchAdminProducts(filters, pageToLoad);
            setProducts((prev) => (append ? [...prev, ...(response.data || [])] : response.data || []));
            setHasNext(!!response.hasNext);
            setPage(pageToLoad);
        } catch (err) {
            setError(err.message);
            if (!append) {
                setProducts([]);
                setHasNext(false);
            }
        } finally {
            setProductsLoading(false);
            setLoadingMore(false);
        }
    }, []);

    const reloadProductList = useCallback(() => {
        return fetchProductsPage(0, appliedFiltersRef.current, false);
    }, [fetchProductsPage]);

    const refreshCategories = useCallback(async () => {
        const categoryResponse = await getAdminCategories();
        setCategories(categoryResponse.data || []);
    }, []);

    const refreshCountries = useCallback(async () => {
        const countryResponse = await getCountries();
        setCountries(countryResponse.data || []);
    }, []);

    const refreshLotNames = useCallback(async () => {
        const response = await getAdminLotNames();
        setLotNames(response.data || []);
    }, []);

    const loadProductsSection = useCallback(async () => {
        setError("");
        try {
            await refreshCategories();
            await refreshCountries();
            await refreshLotNames();
            await fetchProductsPage(0, appliedFiltersRef.current, false);
        } catch (err) {
            setError(err.message);
        }
    }, [fetchProductsPage, refreshCategories, refreshCountries, refreshLotNames]);

    const applyFilters = async () => {
        const nextFilters = { ...draftFilters };
        setAppliedFilters(nextFilters);
        await fetchProductsPage(0, nextFilters, false);
    };

    const resetFilters = async () => {
        setDraftFilters(EMPTY_PRODUCT_FILTERS);
        setAppliedFilters(EMPTY_PRODUCT_FILTERS);
        await fetchProductsPage(0, EMPTY_PRODUCT_FILTERS, false);
    };

    const loadMore = useCallback(() => {
        fetchProductsPage(page + 1, appliedFiltersRef.current, true);
    }, [fetchProductsPage, page]);

    const sentinelRef = useInfiniteScroll({
        hasNext: activeSection === "products" && hasNext,
        loading: productsLoading,
        loadingMore,
        onLoadMore: loadMore
    });

    const activateSection = async (section) => {
        setError("");
        setMessage("");
        setProductViewMode("list");
        setSelectedProduct(null);
        setEditingId(null);
        setForm(emptyProduct);
        setActiveSection(section);

        if (section === "products") {
            await loadProductsSection();
        }
    };

    useEffect(() => {
        const section = searchParams.get("section");
        const resolvedSection = section === "form"
            ? "products"
            : section === "website"
                ? "shops"
            : (menuItems.some((item) => item.id === section)
                ? section
                : ADMIN_PROFILE_DEFAULT_SECTION);
        if (resolvedSection !== section) {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set("section", resolvedSection);
                return next;
            }, { replace: true });
            return;
        }
        if (!staffMenuSectionAllowed(role, resolvedSection)) {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set("section", ADMIN_PROFILE_DEFAULT_SECTION);
                return next;
            }, { replace: true });
            return;
        }
        if (resolvedSection === activeSection) {
            return;
        }
        activateSection(resolvedSection);
    }, [searchParams, activeSection, role, menuItems]);

    const selectSection = (section) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set("section", section);
            next.delete("customer");
            next.delete("orderId");
            return next;
        }, { replace: true });
    };

    const openProductView = (product) => {
        setSelectedProduct(product);
        setEditingId(null);
        setForm(emptyProduct);
        setError("");
        setMessage("");
        setProductViewMode("view");
    };

    const startEdit = async (product) => {
        setSelectedProduct(product);
        setEditingId(product.id);
        setForm(productToForm(product));
        setError("");
        setMessage("");
        setProductViewMode("edit");
        try {
            await refreshCategories();
        } catch (err) {
            setError(err.message);
        }
    };

    const startCreateProduct = async () => {
        setSelectedProduct(null);
        setEditingId(null);
        setForm(emptyProduct);
        setError("");
        setMessage("");
        setProductViewMode("create");
        try {
            await refreshCategories();
        } catch (err) {
            setError(err.message);
        }
    };

    const backToProductList = () => {
        setProductViewMode("list");
        setSelectedProduct(null);
        setEditingId(null);
        setForm(emptyProduct);
        setError("");
    };

    const cancelProductForm = () => {
        if (productViewMode === "edit" && selectedProduct) {
            setEditingId(null);
            setForm(emptyProduct);
            setError("");
            setProductViewMode("view");
            return;
        }
        backToProductList();
    };

    const handleFileUpload = async (event) => {
        const files = Array.from(event.target.files || []);
        if (!files.length) return;
        try {
            const uploadedIds = [];
            for (const file of files) {
                const data = await uploadFile(file);
                uploadedIds.push(data.fileId);
            }
            setForm((current) => ({
                ...current,
                photoIds: [...current.photoIds, ...uploadedIds]
            }));
            setMessage(files.length > 1 ? "Images téléversées" : "Image téléversée");
        } catch (err) {
            setError(err.message);
        } finally {
            event.target.value = "";
        }
    };

    const removePhoto = (photoId) => {
        setForm((current) => ({
            ...current,
            photoIds: current.photoIds.filter((id) => id !== photoId)
        }));
    };

    const movePhoto = (photoId, direction) => {
        setForm((current) => {
            const index = current.photoIds.indexOf(photoId);
            if (index === -1) return current;
            const nextIndex = index + direction;
            if (nextIndex < 0 || nextIndex >= current.photoIds.length) return current;
            const photoIds = [...current.photoIds];
            [photoIds[index], photoIds[nextIndex]] = [photoIds[nextIndex], photoIds[index]];
            return { ...current, photoIds };
        });
    };

    const updateVolumeTier = (index, field, value) => {
        setForm((current) => {
            const volumePrices = current.volumePrices.map((tier, tierIndex) => (
                tierIndex === index ? { ...tier, [field]: value } : tier
            ));
            const next = { ...current, volumePrices };
            if (field === "price" && Number(volumePrices[0]?.minQuantity) === 1) {
                next.price = value;
            }
            return next;
        });
    };

    const addVolumeTier = () => {
        setForm((current) => ({
            ...current,
            volumePrices: [...current.volumePrices, { minQuantity: "", maxQuantity: "", price: "" }]
        }));
    };

    const removeVolumeTier = (index) => {
        setForm((current) => {
            if (current.volumePrices.length <= 1) {
                return current;
            }
            return {
                ...current,
                volumePrices: current.volumePrices.filter((_, tierIndex) => tierIndex !== index)
            };
        });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const hasVolumePricing = !!form.hasVolumePricing;
        let volumePrices;
        let price = Number(form.price);
        if (hasVolumePricing) {
            const rangeError = validateVolumePriceRanges(form.volumePrices);
            if (rangeError) {
                setError(rangeError);
                return;
            }
            volumePrices = form.volumePrices.map((tier) => ({
                minQuantity: Number(tier.minQuantity),
                maxQuantity: tier.maxQuantity === "" || tier.maxQuantity == null
                    ? undefined
                    : Number(tier.maxQuantity),
                price: Number(tier.price)
            }));
            const baseTier = volumePrices.find((tier) => tier.minQuantity === 1);
            price = baseTier.price;
        }
        const payload = {
            title: form.title,
            description: form.description,
            price,
            hasVolumePricing,
            volumePrices: hasVolumePricing ? volumePrices : undefined,
            stockQuantity: editingId ? undefined : Number(form.stockQuantity),
            category: form.category.trim(),
            countryOfOriginId: form.countryOfOriginId || null,
            showCountryBannerOnDetail: !!form.showCountryBannerOnDetail,
            photoIds: form.photoIds,
            status: form.status
        };
        try {
            if (editingId) {
                await updateProduct(editingId, payload);
                setMessage("Produit mis à jour");
                const snapshot = formToProductSnapshot(editingId, {
                    ...form,
                    price,
                    volumePrices: hasVolumePricing
                        ? volumePrices.map((tier) => ({
                            minQuantity: tier.minQuantity,
                            maxQuantity: tier.maxQuantity ?? "",
                            price: tier.price
                        }))
                        : form.volumePrices,
                    hasVolumePricing
                }, selectedProduct, countries);
                setSelectedProduct(snapshot);
                setEditingId(null);
                setForm(emptyProduct);
                setProductViewMode("view");
            } else {
                const created = await createProduct(payload);
                const product = created.data;
                setMessage("Produit créé — ajoutez des lots si besoin");
                setSelectedProduct(product);
                setEditingId(product.id);
                setForm(productToForm(product));
                setProductViewMode("edit");
            }
            await reloadProductList();
            await refreshCategories();
        } catch (err) {
            setError(err.message);
        }
    };

    const handlePublish = async (productId) => {
        try {
            await publishProduct(productId);
            await reloadProductList();
            setSelectedProduct((current) => (
                current && current.id === productId
                    ? { ...current, status: "READY" }
                    : current
            ));
            setMessage("Produit publié");
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDelete = async (productId) => {
        try {
            await deleteProduct(productId);
            setPendingDeleteProductId(null);
            setMessage("Produit supprimé");
            if (selectedProduct?.id === productId || editingId === productId) {
                backToProductList();
            }
            await reloadProductList();
        } catch (err) {
            setError(err.message);
        }
    };

    const buildProductMenuItems = (product, { includeView = true } = {}) => {
        const items = [];

        if (includeView) {
            items.push({
                id: "view",
                label: "Voir les détails",
                onClick: () => openProductView(product)
            });
        }

        items.push({
            id: "edit",
            label: "Modifier",
            onClick: () => startEdit(product)
        });

        if (allowProductPriceAndStatus && product.status !== "READY") {
            items.push({
                id: "publish",
                label: "Publier",
                onClick: () => handlePublish(product.id)
            });
        }

        if (allowProductDelete) {
            items.push({
                id: "delete",
                label: "Supprimer",
                danger: true,
                onClick: () => setPendingDeleteProductId(product.id)
            });
        }

        return items;
    };

    const pendingDeleteProduct = products.find((product) => product.id === pendingDeleteProductId)
        || (selectedProduct?.id === pendingDeleteProductId ? selectedProduct : null);

    const productPhotoIds = (product) => (
        product?.photoIds?.length
            ? product.photoIds
            : (product?.photoId ? [product.photoId] : [])
    );

    const refreshSelectedProductStock = async (productId) => {
        await reloadProductList();
        const response = await searchAdminProducts(appliedFiltersRef.current, 0);
        const next = (response.data || []).find((p) => p.id === productId);
        if (!next) {
            return;
        }
        setSelectedProduct(next);
        setForm((current) => (
            editingId === productId
                ? { ...current, stockQuantity: next.stockQuantity }
                : current
        ));
    };

        return (
        <>
            <ProfileLayout
                title={`Espace personnel — ${STAFF_ROLE_LABELS[role] || "Personnel"}`}
                menuItems={menuItems}
                activeSection={activeSection}
                onSelectSection={selectSection}
                message={message}
                error={error}
            >
                {!activeSection && <div className="profile-empty" />}

                {activeSection === "orders" && (
                    <AdminOrdersSection setMessage={setMessage} setError={setError} />
                )}

                {activeSection === "lots" && (
                    <AdminLotsSection setMessage={setMessage} setError={setError} />
                )}

                {activeSection === "reviews" && (
                    <AdminReviewsSection setMessage={setMessage} setError={setError} />
                )}

                {activeSection === "advertisements" && (
                    <AdminAdvertisementsSection setMessage={setMessage} setError={setError} />
                )}

                {activeSection === "customers" && (
                    <AdminCustomersSection setMessage={setMessage} setError={setError} />
                )}

                {activeSection === "carts" && (
                    <AdminCartsSection setMessage={setMessage} setError={setError} />
                )}

                {activeSection === "voeux" && (
                    <AdminVoeuxSection setMessage={setMessage} setError={setError} />
                )}

                {activeSection === "shops" && (
                    <AdminWebsiteSection section="shops" />
                )}

                {activeSection === "legal" && (
                    <AdminWebsiteSection section="legal" />
                )}

                {activeSection === "employees" && (
                    <AdminEmployeesSection setMessage={setMessage} setError={setError} />
                )}

                {(activeSection === "profile" || activeSection === "password") && (
                    <AdminAccountSection
                        section={activeSection}
                        setMessage={setMessage}
                        setError={setError}
                    />
                )}

                {activeSection === "products" && (
                    <section className="card profile-section profile-section--catalog">
                        {(productViewMode === "create" || productViewMode === "edit") ? (
                            <>
                                <div className="section-header">
                                    <div>
                                        <BackLink onClick={cancelProductForm} className="purchase-detail-back">
                                            {productViewMode === "edit" ? "Retour au détail" : "Retour à la liste"}
                                        </BackLink>
                                        <h2>{editingId ? "Modifier le produit" : "Ajouter un produit"}</h2>
                                    </div>
                                </div>
                                <form onSubmit={handleSubmit}>
                                    <label className="field field--title">
                                        Titre
                                        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
                                    </label>
                                    <label className="field">
                                        Description
                                        <textarea rows="4" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required />
                                    </label>
                                    <IOSToggle
                                        id="product-has-volume-pricing"
                                        label="Tarif dégressif selon la quantité"
                                        checked={!!form.hasVolumePricing}
                                        disabled={!allowProductPriceAndStatus && !!editingId}
                                        onChange={(enabled) => {
                                            if (!allowProductPriceAndStatus && editingId) {
                                                return;
                                            }
                                            setForm((current) => ({
                                                ...current,
                                                hasVolumePricing: enabled,
                                                volumePrices: enabled
                                                    ? (current.volumePrices?.length
                                                        ? current.volumePrices
                                                        : [{ minQuantity: 1, maxQuantity: "", price: current.price || "" }])
                                                    : [{ minQuantity: 1, maxQuantity: "", price: current.price || "" }]
                                            }));
                                        }}
                                    />
                                    {!form.hasVolumePricing ? (
                                        <label className="field field--amount">
                                            Prix de vente (FCFA)
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={form.price}
                                                onChange={(e) => setForm({ ...form, price: e.target.value })}
                                                required
                                                disabled={!allowProductPriceAndStatus && !!editingId}
                                            />
                                        </label>
                                    ) : (
                                        <div className="admin-volume-pricing">
                                            <p className="muted">
                                                Définissez des plages de quantité sans chevauchement
                                                (ex. 1–4, 5–9, 10+). Laissez « Qté max. » vide pour illimité.
                                                Un palier couvrant la quantité 1 est obligatoire.
                                            </p>
                                            {form.volumePrices.map((tier, index) => (
                                                <div key={`tier-${index}`} className="admin-volume-pricing-row">
                                                    <label className="field field--qty">
                                                        Qté min.
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            step="1"
                                                            value={tier.minQuantity}
                                                            onChange={(e) => updateVolumeTier(index, "minQuantity", e.target.value)}
                                                            required
                                                            disabled={!allowProductPriceAndStatus && !!editingId}
                                                        />
                                                    </label>
                                                    <label className="field field--qty">
                                                        Qté max. (optionnel)
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            step="1"
                                                            value={tier.maxQuantity ?? ""}
                                                            onChange={(e) => updateVolumeTier(index, "maxQuantity", e.target.value)}
                                                            placeholder="Illimité"
                                                            disabled={!allowProductPriceAndStatus && !!editingId}
                                                        />
                                                    </label>
                                                    <label className="field field--amount">
                                                        Prix unitaire (FCFA)
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            value={tier.price}
                                                            onChange={(e) => updateVolumeTier(index, "price", e.target.value)}
                                                            required
                                                            disabled={!allowProductPriceAndStatus && !!editingId}
                                                        />
                                                    </label>
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary btn-inline"
                                                        onClick={() => removeVolumeTier(index)}
                                                        disabled={
                                                            form.volumePrices.length <= 1
                                                            || (!allowProductPriceAndStatus && !!editingId)
                                                        }
                                                    >
                                                        Retirer
                                                    </button>
                                                </div>
                                            ))}
                                            {!(!allowProductPriceAndStatus && editingId) && (
                                                <button type="button" className="btn btn-secondary btn-inline" onClick={addVolumeTier}>
                                                    Ajouter un palier
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    <CategoryField
                                        value={form.category}
                                        categories={categories}
                                        onChange={(category) => setForm({ ...form, category })}
                                    />
                                    <CountryField
                                        value={form.countryOfOriginId}
                                        countries={countries}
                                        onChange={(countryOfOriginId) => setForm({
                                            ...form,
                                            countryOfOriginId,
                                            showCountryBannerOnDetail: countryOfOriginId
                                                ? form.showCountryBannerOnDetail
                                                : false
                                        })}
                                    />
                                    <IOSToggle
                                        id="show-country-banner-on-detail"
                                        label="Bandeau d'origine sur la fiche produit"
                                        description="Affiche le drapeau du pays d'origine en haut de l'image sur la page détail (si un pays est renseigné)."
                                        checked={form.showCountryBannerOnDetail}
                                        disabled={!form.countryOfOriginId}
                                        onChange={(showCountryBannerOnDetail) => setForm({ ...form, showCountryBannerOnDetail })}
                                    />
                                    {editingId ? (
                                        <p className="muted">
                                            Stock actuel : <strong>{form.stockQuantity}</strong>
                                            {" "}(somme des lots ci-dessous)
                                        </p>
                                    ) : (
                                        <label className="field field--qty">
                                            Quantité initiale en stock
                                            <input
                                                type="number"
                                                min="0"
                                                value={form.stockQuantity}
                                                onChange={(e) => setForm({ ...form, stockQuantity: e.target.value })}
                                                required
                                            />
                                            <span className="muted">
                                                Si &gt; 0, un lot « Lot initial » est créé (prix de revient 0). Ajoutez d&apos;autres lots ensuite.
                                            </span>
                                        </label>
                                    )}
                                    <label className="field field--status">
                                        Statut
                                        <select
                                            value={form.status}
                                            onChange={(e) => setForm({ ...form, status: e.target.value })}
                                            disabled={!allowProductPriceAndStatus}
                                        >
                                            {PRODUCT_STATUSES.map((status) => (
                                                <option key={status} value={status}>{formatProductStatus(status)}</option>
                                            ))}
                                        </select>
                                    </label>
                                    {editingId ? (
                                        <AdminProductLots
                                            productId={editingId}
                                            setMessage={setMessage}
                                            setError={setError}
                                            onStockChanged={() => refreshSelectedProductStock(editingId)}
                                        />
                                    ) : null}
                                    <label className="field">
                                        Images du produit
                                        <input type="file" accept="image/*" multiple onChange={handleFileUpload} />
                                    </label>
                                    {form.photoIds.length > 0 && (
                                        <div className="admin-photo-gallery">
                                            {form.photoIds.map((photoId, index) => (
                                                <div key={photoId} className="admin-photo-item">
                                                    <img src={`/api/v1/files/${photoId}`} alt={`Aperçu ${index + 1}`} />
                                                    <div className="admin-photo-actions">
                                                        <button
                                                            type="button"
                                                            className="btn btn-secondary btn-small"
                                                            disabled={index === 0}
                                                            onClick={() => movePhoto(photoId, -1)}
                                                        >
                                                            ←
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-secondary btn-small"
                                                            disabled={index === form.photoIds.length - 1}
                                                            onClick={() => movePhoto(photoId, 1)}
                                                        >
                                                            →
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-secondary btn-small"
                                                            onClick={() => removePhoto(photoId)}
                                                        >
                                                            Retirer
                                                        </button>
                                                    </div>
                                                    {index === 0 && <span className="admin-photo-primary">Principale</span>}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <div className="profile-form-actions profile-form-actions--end">
                                        <button className="btn btn-primary btn-inline" type="submit">
                                            {editingId ? "Enregistrer les modifications" : "Créer le produit"}
                                        </button>
                                        <button type="button" className="btn btn-secondary btn-inline" onClick={cancelProductForm}>
                                            Annuler
                                        </button>
                                    </div>
                                </form>
                            </>
                        ) : productViewMode === "view" && selectedProduct ? (
                            <>
                                <div className="section-header">
                                    <div>
                                        <BackLink onClick={backToProductList} className="purchase-detail-back">
                                            Retour à la liste
                                        </BackLink>
                                        <h2>Détails du produit</h2>
                                    </div>
                                    <RowActionsMenu
                                        ariaLabel={`Actions pour ${selectedProduct.title}`}
                                        items={buildProductMenuItems(selectedProduct, { includeView: false })}
                                    />
                                </div>

                                <ProductImageGallery
                                    className="product-gallery--detail"
                                    photoIds={productPhotoIds(selectedProduct)}
                                    title={selectedProduct.title}
                                    placeholder="Aucune image"
                                />

                                <dl className="profile-detail-view profile-detail-view--compact">
                                    <div className="profile-detail-row">
                                        <dt>Titre</dt>
                                        <dd>{selectedProduct.title}</dd>
                                    </div>
                                    <div className="profile-detail-row">
                                        <dt>Description</dt>
                                        <dd>{selectedProduct.description || "—"}</dd>
                                    </div>
                                    <div className="profile-detail-row">
                                        <dt>Catégorie</dt>
                                        <dd>{selectedProduct.category || "—"}</dd>
                                    </div>
                                    <div className="profile-detail-row">
                                        <dt>Pays d&apos;origine</dt>
                                        <dd className="profile-detail-country-value">
                                            {selectedProduct.countryOfOrigin ? (
                                                <>
                                                    <CountryFlag country={selectedProduct.countryOfOrigin} size="sm" />
                                                    <span>{countryDisplayName(selectedProduct.countryOfOrigin)}</span>
                                                </>
                                            ) : "—"}
                                        </dd>
                                    </div>
                                    <div className="profile-detail-row">
                                        <dt>Bandeau d&apos;origine (fiche)</dt>
                                        <dd>{selectedProduct.showCountryBannerOnDetail ? "Activé" : "Désactivé"}</dd>
                                    </div>
                                    <div className="profile-detail-row">
                                        <dt>Statut</dt>
                                        <dd>
                                            <span className={productStatusClassName(selectedProduct.status)}>
                                                {formatProductStatus(selectedProduct.status)}
                                            </span>
                                        </dd>
                                    </div>
                                    <div className="profile-detail-row">
                                        <dt>Prix</dt>
                                        <dd>
                                            {selectedProduct.hasVolumePricing && selectedProduct.volumePrices?.length ? (
                                                <ul className="profile-phone-view-list">
                                                    {selectedProduct.volumePrices.map((tier, index) => (
                                                        <li key={`view-tier-${index}`}>
                                                            {tier.minQuantity}
                                                            {tier.maxQuantity != null ? `–${tier.maxQuantity}` : "+"}
                                                            {" : "}
                                                            <Price value={tier.price} />
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <Price value={selectedProduct.price} />
                                            )}
                                        </dd>
                                    </div>
                                    <div className="profile-detail-row">
                                        <dt>Stock</dt>
                                        <dd>{selectedProduct.stockQuantity ?? 0}</dd>
                                    </div>
                                </dl>

                                <AdminProductLots
                                    productId={selectedProduct.id}
                                    setMessage={setMessage}
                                    setError={setError}
                                    onStockChanged={() => refreshSelectedProductStock(selectedProduct.id)}
                                />
                            </>
                        ) : (
                        <div className="catalog-with-filters">
                            <ProductFilterToolbox
                                filters={draftFilters}
                                categories={categories}
                                lotNames={lotNames}
                                showStatus
                                showLotName
                                showStock
                                loading={productsLoading && !loadingMore}
                                onChange={setDraftFilters}
                                onApply={applyFilters}
                                onReset={resetFilters}
                            />

                            <div className="catalog-results">
                                <div className="catalog-results-header">
                                    <div className="catalog-results-header-main">
                                        <h2>Produits</h2>
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-inline catalog-results-create-btn"
                                            onClick={startCreateProduct}
                                        >
                                            Ajouter un produit
                                        </button>
                                    </div>
                                    {!productsLoading && (
                                        <p className="muted catalog-results-count">
                                            {products.length} produit{products.length > 1 ? "s" : ""} affiché{products.length > 1 ? "s" : ""}
                                            {hasNext ? " · faites défiler pour en voir plus" : ""}
                                        </p>
                                    )}
                                </div>

                                {productsLoading && <p className="muted">Chargement des produits...</p>}

                                {!productsLoading && products.length === 0 && (
                                    <p className="muted">Aucun produit ne correspond à votre recherche.</p>
                                )}

                                <div className="admin-table purchase-list">
                                    {products.map((product) => (
                                        <div key={product.id} className="admin-row admin-list-row purchase-list-row">
                                            <button
                                                type="button"
                                                className="admin-list-row-main purchase-list-row-main"
                                                onClick={() => openProductView(product)}
                                            >
                                                <div className="purchase-list-row-top">
                                                    <ProductImageGallery
                                                        className="product-gallery--compact"
                                                        photoIds={productPhotoIds(product)}
                                                        title={product.title}
                                                        placeholder="Aucune image"
                                                    />
                                                    <div className="purchase-list-row-heading">
                                                        <strong>{product.title}</strong>
                                                        <p className="muted">
                                                            <span className={productStatusClassName(product.status)}>
                                                                {formatProductStatus(product.status)}
                                                            </span>
                                                            {" · "}
                                                            <Price value={lowestProductPrice(product)} />
                                                        </p>
                                                    </div>
                                                    <span className="admin-list-row-chevron" aria-hidden="true">›</span>
                                                </div>
                                                <p className="muted purchase-fulfillment">
                                                    {product.category}
                                                    {product.hasVolumePricing ? " · tarif dégressif" : ""}
                                                </p>
                                            </button>
                                            <RowActionsMenu
                                                ariaLabel={`Actions pour ${product.title}`}
                                                items={buildProductMenuItems(product)}
                                            />
                                        </div>
                                    ))}
                                </div>

                                {loadingMore && <p className="muted catalog-loading-more">Chargement de la suite...</p>}
                                <div ref={sentinelRef} className="catalog-scroll-sentinel" aria-hidden="true" />
                            </div>
                        </div>
                        )}
                    </section>
                )}
            </ProfileLayout>
            <ConfirmDialog
                open={!!pendingDeleteProductId}
                title="Supprimer le produit"
                message={
                    pendingDeleteProduct
                        ? `Voulez-vous vraiment supprimer « ${pendingDeleteProduct.title} » ? Cette action est irréversible.`
                        : "Voulez-vous vraiment supprimer ce produit ? Cette action est irréversible."
                }
                confirmLabel="Supprimer"
                dangerous
                onConfirm={() => handleDelete(pendingDeleteProductId)}
                onCancel={() => setPendingDeleteProductId(null)}
            />
        </>
    );
}
